import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import {
  AiOutlineCheckCircle,
  AiOutlineSafetyCertificate,
  AiOutlineThunderbolt,
} from "react-icons/ai";
import {
  Hero1,
  Hero2,
  Hero3,
  Hero4,
  Pharmaceutical,
  Petrochemical, 
  Paper,
  Mining,
  Automobile,
  Beverages,
  Oil,
  Marine,
  Chemical, 
  Agriculture,
  Powerplant,
  Textile,
  Building
} from "../assets";

function Home() {
  const [products, setProducts] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // Add these animation variants at the top (below imports)
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};


  useEffect(() => {
  const fetchProducts = () => {
    axios.get("https://api.microwellindustries.com/api/products").then((res) => {
      const activeProducts = res.data.filter((p) => p.status === "Active");
      setProducts(activeProducts.slice(0, 8));
    });
  };

  fetchProducts();

  const handlePop = () => {
    fetchProducts();
  };

  window.addEventListener("popstate", handlePop);

  return () => {
    window.removeEventListener("popstate", handlePop);
  };
}, []);


  const handleViewProduct = (slug) => {
    navigate(`/product/${slug}`);
  };

  const slides = [
    {
      id: 1,
      src: Hero1,
      type: "image",
      title: "Powering Performance, Sealing & Pumping Trust",
      subtitle: "Shaping the future of industries with innovation and sustainable solutions",
      textColor: "text-white"
    },
    {
      id: 2,
      src: Hero2,
      type: "video",
      title: "Engineering Reliability in Every Seal",
      subtitle: "Quality-driven Mechanical Shaft Seals and components trusted by industries",
      textColor: "text-red-400"
    },
    {
      id: 3,
      src: Hero3,
      type: "image",
      title: "Your Partner in Seals, Pumps and Industrial Solutions",
      subtitle: "We ensuring performance under every challenge",
      textColor: "text-black"
    },
    {
      id: 4,
      src: Hero4,
      type: "video",
      title: "Trusted in Pumps. Proven in Performance.",
      subtitle: "We delivers reliable pumps and precision components built to last",
      textColor: "text-blue-500"
    },
  ];

  const industries = [
    { id: 1, name: "Automobile Industry", img: Automobile },
    { id: 2, name: "Oil & Gas Industry", img: Oil },
    { id: 3, name: "Agriculture Industry", img: Agriculture },
    { id: 4, name: "Textile Industry", img: Textile },
    { id: 5, name: "Chemical Industry", img: Chemical },
    { id: 6, name: "Pulp & Paper Industry", img: Paper },
    { id: 7, name: "Pharmaceutical Industry", img: Pharmaceutical },
    { id: 8, name: "Petrochemical Industry", img: Petrochemical },
    { id: 9, name: "Power Plant Industry", img: Powerplant },
    { id: 10, name: "Mining Industry", img: Mining },
    { id: 11, name: "Beverages Industry", img: Beverages },
    { id: 12, name: "Marine Industry", img: Marine },
  ];

  return (
    <div>
      {/* Hero Section Desktop View */}
      <div className="hidden md:block">
        <Swiper
          modules={[Autoplay, Navigation, Pagination]}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          navigation
          pagination={{ clickable: true }}
          loop
          onSlideChange={(swiper) => setActiveSlide(swiper.realIndex)}
          className="w-full"
        >
          {slides.map((slide,index) => (
            <SwiperSlide key={slide.id}>
              {slide.type === "image" ? (
                <img
                  src={slide.src}
                  alt={slide.title}
                  className="w-full h-[500px] object-cover"
                />
              ) : (
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-[500px] object-cover"
                >
                  <source src={slide.src} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}
              {activeSlide === index && (
                <motion.div
                  className={`absolute inset-0 flex flex-col items-center justify-center text-center px-4 ${slide.textColor}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1 }}
                >
                  <h2 className="text-3xl font-bold">{slide.title}</h2>
                  <p className="text-lg">{slide.subtitle}</p>
                </motion.div>
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Hero Section Mobile View */}
      <div className="block md:hidden">
        <Swiper
          modules={[Autoplay, Pagination]}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          loop
          onSlideChange={(swiper) => setActiveSlide(swiper.realIndex)}
          className="w-full"
        >
          {slides.map((slide,index) => (
            <SwiperSlide key={slide.id}>
              {slide.type === "image" ? (
                <img
                  src={slide.src}
                  alt={slide.title}
                  className="w-full h-[400px] object-cover"
                />
              ) : (
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-[400px] object-cover"
                >
                  <source src={slide.src} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
              )}
              {activeSlide === index && (
                <motion.div
                  className={`absolute inset-0 flex flex-col items-center justify-center text-center px-4 ${slide.textColor}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1 }}
                >
                  <h2 className="text-xl font-bold">{slide.title}</h2>
                  <p className="text-xs">{slide.subtitle}</p>
                </motion.div>
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Info Section */}
      <section className="py-7 px-4 md:px-12 bg-white">
        <div className="max-w-screen-xl mx-auto grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="text-blue-600 font-semibold text-sm mb-2">
              GET THE RIGHT SOLUTION FOR
            </h3>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Mechanical Shaft Seals, Pump & Pumps Component
            </h2>

            <p className="text-gray-600 leading-relaxed mb-6">
              We have been one of the leading and most experienced
              manufacturers, exporters, and trusted suppliers in the Mechanical Shaft Seals, Pump & Pumps Component
              industry for the last 15 years, as MICRO-WELL INDUSTRIES.
            </p>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <AiOutlineCheckCircle className="text-blue-500 w-6 h-6 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-800">
                    Quality Assurance
                  </h4>
                  <p className="text-gray-600 text-sm">
                    All product verify by QC Department.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <AiOutlineSafetyCertificate className="text-blue-500 w-6 h-6 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-800">
                    Certified Products
                  </h4>
                  <p className="text-gray-600 text-sm">
                    All products are certified by ISO Certification.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <AiOutlineThunderbolt className="text-blue-500 w-6 h-6 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-800">Quick Service</h4>
                  <p className="text-gray-600 text-sm">
                    Quick Service regarding our product at door steps.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div>
            <img
              src={Building}
              alt="Company Building"
              className="rounded-2xl shadow-lg object-cover w-full"
            />
          </div>
        </div>
      </section>

      {/* Our Products */}
<section className="py-7 px-4 md:px-12 bg-gray-50">
  <div className="max-w-screen-xl mx-auto">
    <h2 className="text-4xl font-bold text-center mb-8">Our Products</h2>

    <motion.div
      key={location.key}
      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6"
      variants={containerVariants}
      initial="hidden"
      animate="show"
    >
      {products.map((product) => (
        <motion.div key={product._id} variants={itemVariants}>
          <div
            onClick={() => handleViewProduct(product.slug)}
            className="w-full h-full bg-white rounded-2xl shadow-lg hover:shadow-2xl 
                       transition-all duration-300 group overflow-hidden flex flex-col cursor-pointer"
          >
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-contain transform group-hover:scale-105 transition duration-500"
              />
              <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 
                              transition duration-300 flex items-center justify-center">
                <span className="text-white text-lg font-semibold">
                  View Details
                </span>
              </div>
            </div>
            <div className="p-4 flex-1 flex flex-col justify-between">
              <div>
                <span className="text-xs font-semibold text-blue-600 uppercase">
                  Type : {product.type}
                </span>
                <h3 className="text-xl font-bold text-gray-800 mt-2">
                  {product.name}
                </h3>
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </motion.div>

    {/* More Products Button */}
    <div className="flex justify-center mt-10">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        onClick={() => navigate("/products")}
        className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-full shadow-md hover:bg-blue-700 transition-colors"
      >
        More Products
      </motion.button>
    </div>
  </div>
</section>


      {/* Industries Served */}
      <section className="py-3 px-4 md:px-12 pb-10">
        <div className="max-w-screen-xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-10">
            Industries We Serve
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {industries.map((industry) => (
              <div
                key={industry.id}
                className="rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow"
              >
                <img
                  src={industry.img}
                  alt={industry.name}
                  className="w-full h-40 object-cover"
                />
                <div className="p-4 bg-white text-center">
                  <h3 className="text-lg font-semibold text-gray-700">
                    {industry.name}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
